/**
 * "View as teacher" — drop admin privileges for the rest of this tab, so an
 * admin can design and QA what a teacher actually sees.
 *
 * Several surfaces read `diffitUser.is_diffit_admin` and quietly behave
 * differently for staff. The referral modal is the one that started this: its
 * "See your rewards" link is gated on `is_diffit_admin || the campaign is
 * pointed at a program`, so an admin sees the link in every environment while a
 * teacher does not — which makes an admin the one person who cannot review that
 * card as shipped.
 *
 * Applied once, where diffitUser enters the app's context (base.jsx), so every
 * consumer agrees: the profile menu's admin items, the /admin route guard, Eval,
 * and the referral gate all see a teacher together. Masking it per-surface would
 * produce a half-teacher, which is worse for QA than not having this at all.
 *
 * ─── Scope, and what this does NOT do ───────────────────────────────────────
 * This is a CLIENT-side mask on one boolean. The server decides staff by email
 * (diffit-api's app/constants/admins.py), so admin-only endpoints, unpublished
 * landing-page variants and anything else gated server-side still answer as
 * staff. It shows you a teacher's UI, not a teacher's API.
 *
 * It is also inert for anyone who is not already an admin — masking false to
 * false changes nothing — so the param is harmless if it ever leaks.
 */
const KEY = 'diffit_view_as_teacher';

export const VIEW_AS_TEACHER_PARAM = 'viewAsTeacher';

const read = () => {
  try {
    return sessionStorage.getItem(KEY) === '1';
  } catch {
    return false;
  }
};

const write = (on) => {
  try {
    if (on) sessionStorage.setItem(KEY, '1');
    else sessionStorage.removeItem(KEY);
  } catch {
    // A browser with storage blocked just does not keep the setting.
  }
};

/**
 * Resolve the setting for this render, honouring `?viewAsTeacher=1` / `=0`.
 *
 * Per tab rather than per URL: QA means clicking around, and a setting that
 * fell off at the first navigation would be useless. Closing the tab clears it,
 * which is the escape hatch when someone forgets it is on.
 */
export const resolveViewAsTeacher = (searchParams) => {
  const param = searchParams?.get?.(VIEW_AS_TEACHER_PARAM);
  if (param === '1' || param === 'true') {
    write(true);
    return true;
  }
  if (param === '0' || param === 'false') {
    write(false);
    return false;
  }
  return read();
};

/**
 * The user as the app should see them. Returns the same object reference when
 * nothing is masked, so this cannot churn a context value.
 */
export const applyViewAsTeacher = (diffitUser, isViewingAsTeacher) => {
  if (!isViewingAsTeacher || !diffitUser?.is_diffit_admin) return diffitUser;
  return { ...diffitUser, is_diffit_admin: false };
};
